import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast, { Toaster } from "react-hot-toast";
import MUIDataTable from 'mui-datatables';
import CircularProgress from "@mui/material/CircularProgress";
import { connect } from 'react-redux';

const CategoryWaitTimeStatistics = (props) => {

    const baseUrl = localStorage.getItem('API_URL');
    const token = localStorage.getItem('user_token');

    const [categoryData, setCategoryData] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    // Check if dark mode is active
    const isDarkMode = !props.lightVersion;

    // Helper function to convert HH:mm:ss to seconds
    const timeToSeconds = (timeString) => {
        if (!timeString || timeString === '00:00:00') return 0;
        const parts = timeString.split(':');
        if (parts.length !== 3) return 0;
        return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
    };

    // Helper function to format number with thousand separators
    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return parseInt(num).toLocaleString('en-US');
    };

    // Helper function to calculate percentage difference
    const calculatePercentageDiff = (avgTime, targetTime) => {
        const avgSeconds = timeToSeconds(avgTime);
        const targetSeconds = timeToSeconds(targetTime);
        if (targetSeconds === 0) return null;
        return ((avgSeconds - targetSeconds) / targetSeconds) * 100;
    };

    // Helper function to get row background color based on comparison (theme-aware)
    const getRowBackgroundColor = (avgTime, targetTime) => {
        const percentageDiff = calculatePercentageDiff(avgTime, targetTime);
        if (percentageDiff === null) return 'transparent';
        if (isDarkMode) {
            // Dark mode colors
            if (percentageDiff <= 0) return 'rgba(76, 175, 80, 0.2)'; // Green
            if (percentageDiff <= 20) return 'rgba(255, 193, 7, 0.2)'; // Yellow
            return 'rgba(244, 67, 54, 0.2)'; // Red
        } else {
            // Light mode colors
        if (percentageDiff <= 0) return 'rgba(76, 175, 80, 0.1)'; // Green
        if (percentageDiff <= 20) return 'rgba(255, 193, 7, 0.1)'; // Yellow
        return 'rgba(244, 67, 54, 0.1)'; // Red
        }
    };

    const options = {
        tableBodyHeight: '50vh',
        filter: false,
        responsive: 'vertical',
        serverSide: false,
        download: false,
        viewColumns: false,
        jumpToPage: true,
        selectableRows: 'none',
        print: false,
        rowsPerPage: rowsPerPage,
        onChangeRowsPerPage: (n) => {
            setRowsPerPage(n)
        },
        rowsPerPageOptions: [10, 20, 30, 50, 100],
        setTableProps: () => {
            return {
                size: 'small',
            };
        },
        search: true,
        searchPlaceholder: 'Search categories...'
    };

    const columns = [
        {
            label: 'Category Name',
            name: 'category',
            options: {
                sort: true,
                filter: false
            }
        },
        {
            label: 'Token Count',
            name: 'tokenCount',
            options: {
                sort: true,
                filter: false,
                customBodyRender: (value) => {
                    return formatNumber(value);
                }
            }
        },
        {
            label: 'Average Wait Time',
            name: 'avgWaitTime',
            options: {
                sort: true,
                filter: false,
                customBodyRender: (value) => {
                    return <strong>{value || '00:00:00'}</strong>;
                }
            }
        },
        {
            label: 'Max Wait Time',
            name: 'maxWaitTime',
            options: {
                sort: true,
                filter: false
            }
        },
        {
            label: 'Min Wait Time',
            name: 'minWaitTime',
            options: {
                sort: true,
                filter: false
            }
        },
        {
            label: 'Target Wait Time',
            name: 'targetWaitTime',
            options: {
                sort: true,
                filter: false
            }
        }
    ];

    // Fetch category wait time statistics
    const getCategoryWaitTimeStatistics = () => {
        setLoading(true);
        axios({
            method: 'GET',
            url: `${baseUrl}/ticket/category-wait-time-statistics/branch/${props.selectedBranchId}`,
            headers: {
                Authorization: token
            }
        }).then((res) => {
            console.log('getCategoryWaitTimeStatistics success', res.data);
            if (res.data && (res.data.status === 'ok' || res.data.message === 'success') && res.data.data) {
                // Sort by category name alphabetically
                const sortedData = [...res.data.data].sort((a, b) => 
                    a.category.localeCompare(b.category)
                );
                setCategoryData(sortedData);
            } else {
                setCategoryData([]);
            }
            setLoading(false);
        }).catch((error) => {
            console.log('getCategoryWaitTimeStatistics error');
            console.log(error);
            setCategoryData([]);
            setLoading(false);
            
            if (error.response !== undefined) {
                toast.remove();
                toast.error(error.response.data.message || 'Failed to fetch category wait time statistics', {
                    position: 'bottom-center',
                });
            } else {
                toast.remove();
                toast.error('Something went wrong', {
                    position: 'bottom-center',
                });
            }
        });
    };

    useEffect(() => {
        if (props.showCategoryWaitTimeStatistics && props.selectedBranchId) {
            getCategoryWaitTimeStatistics();
        }
    }, [props.showCategoryWaitTimeStatistics, props.selectedBranchId]);

    // Prepare table data
    const tableData = categoryData.map((item) => [
        item.category || 'N/A',
        item.tokenCount || 0,
        item.avgWaitTime || '00:00:00',
        item.maxWaitTime || '00:00:00',
        item.minWaitTime || '00:00:00',
        item.targetWaitTime || '00:00:00'
    ]);

    return (
        <>
            {props.showCategoryWaitTimeStatistics && (
                <div className={`modal fade d-block show modal-container`} style={{ backgroundColor: isDarkMode ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl" style={{ marginTop: '3%' }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Category Wait Time Statistics
                                    {props.avgWaitTime && (
                                        <span className="ml-2" style={{ fontWeight: 'normal', fontSize: '0.9em', color: isDarkMode ? '#aaa' : '#666' }}>
                                            (Avg: {props.avgWaitTime})
                                        </span>
                                    )}
                                </h5>
                                <button
                                    type="button"
                                    className="close"
                                    data-dismiss="modal"
                                    aria-label="Close"
                                    onClick={() => {
                                        props.setShowCategoryWaitTimeStatistics(false);
                                        props.setSticky(false);
                                    }}
                                >
                                    <span aria-hidden="true">×</span>
                                </button>
                            </div>

                            <div className="modal-body">
                                {isLoading ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                                        <CircularProgress />
                                    </div>
                                ) : categoryData.length > 0 ? (
                                    <MUIDataTable
                                        title=""
                                        data={tableData}
                                        columns={columns}
                                        options={{
                                            ...options,
                                            setRowProps: (row, dataIndex, rowIndex) => {
                                                const item = categoryData[rowIndex];
                                                const avgTime = item.avgWaitTime || '00:00:00';
                                                const targetTime = item.targetWaitTime || '00:00:00';
                                                const percentageDiff = calculatePercentageDiff(avgTime, targetTime);
                                                
                                                let title = '';
                                                if (percentageDiff !== null) {
                                                    if (percentageDiff > 0) {
                                                        title = `Average is ${percentageDiff.toFixed(1)}% above target`;
                                                    } else if (percentageDiff < 0) {
                                                        title = `Average is ${Math.abs(percentageDiff).toFixed(1)}% below target`;
                                                    } else {
                                                        title = 'Average equals target';
                                                    }
                                                }
                                                
                                                return {
                                                    style: {
                                                        backgroundColor: getRowBackgroundColor(avgTime, targetTime)
                                                    },
                                                    title: title
                                                };
                                            }
                                        }}
                                    />
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '40px' }}>
                                        <p>No data available</p>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        props.setShowCategoryWaitTimeStatistics(false);
                                        props.setSticky(false);
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Toaster />
        </>
    );
};

const mapStateToProps = state => ({
    lightVersion: state.settings?.lightVersion || false,
});

export default connect(mapStateToProps)(CategoryWaitTimeStatistics);

